import { FC } from "react"

import Skeleton from "../base"
import { cn } from "../../../utils/classnames"
import { leadScoreTableRowGridClass } from "../../lead-scoring/lead-score-table-grid"

interface LeadScoreListSkeletonProps {
  /** Placeholder rows (each mirrors one lead card + detail block). */
  rows?: number
}

const LeadScoreListSkeleton: FC<LeadScoreListSkeletonProps> = ({
  rows = 5
}) => {
  const rowGridClass = leadScoreTableRowGridClass(true)

  return (
    <div
      className="divide-y divide-neutral-400"
      role="status"
      aria-label="Loading lead scores"
    >
      <div className="flex items-center gap-3 border-b border-neutral-200 px-3 py-2.5 sm:px-4">
        <div className="h-4 w-4 shrink-0 rounded border border-neutral-200 bg-neutral-100" />
        <div className="h-3 w-24 rounded-md bg-neutral-200" />
      </div>

      <div
        className={cn(
          rowGridClass,
          "hidden md:grid",
          "py-2",
          "text-sm font-medium uppercase tracking-wide text-neutral-500"
        )}
      >
        <span aria-hidden className="md:min-h-px" />
        <span className="text-left">Score</span>
        <span className="text-left">Lead</span>
        <span className="text-left">Contact</span>
        <span className="text-left">Assigned</span>
        <span className="text-left">Action</span>
      </div>

      {[...Array(rows)].map((_, i) => (
        <Skeleton key={i} className="bg-white">
          <div className="py-4 md:py-5">
            <div className={cn(rowGridClass, "min-w-0")}>
              <div className="mt-2 h-4 w-4 shrink-0 self-start rounded border border-neutral-200 bg-neutral-100" />

              <div className="flex min-w-0 w-full max-w-full items-start gap-3 max-md:col-span-full md:contents">
                <div className="h-[68px] w-[53px] shrink-0 rounded-lg bg-neutral-200" />

                <div className="min-w-0 flex-1 space-y-2 md:min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="h-5 w-40 rounded-md bg-neutral-200" />
                    <div className="h-5 w-20 rounded-md bg-neutral-200" />
                    <div className="h-5 w-14 rounded-md bg-neutral-200" />
                  </div>
                  <div className="h-4 w-[88%] max-w-md rounded-md bg-neutral-200" />
                  <div className="h-4 w-[52%] max-w-xs rounded-md bg-neutral-200" />
                </div>

                <div className="ml-auto shrink-0 self-start rounded-md p-1.5 md:hidden">
                  <div className="h-[22px] w-[22px] rounded bg-neutral-200" />
                </div>
              </div>

              <div
                className={cn(
                  "max-md:col-span-full max-md:flex max-md:w-full max-md:items-start",
                  "md:contents"
                )}
              >
                <div className="flex min-w-0 flex-col gap-2 max-md:min-w-0 max-md:flex-1 max-md:border-r max-md:border-neutral-200">
                  <div className="h-4 w-full max-w-[10rem] rounded-md bg-neutral-200" />
                  <div className="h-4 w-28 rounded-md bg-neutral-200" />
                </div>
                <div className="flex min-w-0 flex-col gap-2 max-md:min-w-0 max-md:flex-1 max-md:pl-4">
                  <div className="h-4 w-32 rounded-md bg-neutral-200" />
                  <div className="h-4 w-20 rounded-md bg-neutral-200" />
                </div>
              </div>

              <div className="flex min-w-0 flex-col items-start gap-2">
                <div className="h-7 w-24 rounded-full bg-neutral-200" />
                <div className="hidden h-4 w-28 rounded-md bg-neutral-200 md:block" />
              </div>
            </div>

            <div className="mt-4">
              <div className="space-y-2">
                <div className="h-3.5 w-full rounded-md bg-neutral-200" />
                <div className="h-3.5 w-[94%] rounded-md bg-neutral-200" />
                <div className="h-3.5 w-[78%] rounded-md bg-neutral-200" />
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-3">
                <div className="mt-0.5 h-5 w-5 shrink-0 rounded bg-neutral-200" />
                <div className="min-w-0 flex-1 space-y-2 pt-0.5">
                  <div className="h-3.5 w-full rounded-md bg-neutral-200" />
                  <div className="h-3.5 w-[96%] rounded-md bg-neutral-200" />
                  <div className="h-3.5 w-full rounded-md bg-neutral-200" />
                  <div className="h-3.5 w-[72%] rounded-md bg-neutral-200" />
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <div className="h-8 w-36 rounded-md border border-neutral-200 bg-neutral-100" />
                <div className="h-8 w-28 rounded-md border border-neutral-200 bg-neutral-100" />
                <div className="h-8 w-40 rounded-md border border-neutral-200 bg-neutral-100" />
                <div className="h-8 w-32 rounded-md border border-neutral-200 bg-neutral-100" />
                <div className="h-8 w-44 rounded-md border border-neutral-200 bg-neutral-100" />
              </div>

              <div className="mt-4 flex items-center gap-2">
                <div className="h-5 w-5 shrink-0 rounded bg-neutral-200" />
                <div className="h-4 w-40 rounded-md bg-neutral-200" />
              </div>
            </div>
          </div>
        </Skeleton>
      ))}
    </div>
  )
}

export default LeadScoreListSkeleton
